import { IFileData } from '@/api/filestorage';
import { Vue } from 'vue-class-component/dist/vue-class-component';
import {
  CommonObject,
  IStorageAcceptDetails,
  IStorageIssueDetails,
} from '@/utils/types';
import { IAdditionalStatements } from '@/utils/types';

interface IDocument {
  attestation?: string;
  description?: string;
  docDate?: string;
  file?: IFileData | null;
  fileSig?: IFileData | null;
  issuer?: string;
  name: string;
  number?: string;
  series?: string;
  type?: string;
  version?: string;
}

interface IAddress {
  fiasCode: string;
  okato: string;
  kladr: string;
  oktmo: string;
  postalCode: string;
  regionCode?: string;
  regionFiasId: string;
  regionName: string;
  fiasLevel: string;
  areaType?: string;
  areaName?: string;
  cityType: string;
  cityName: string;
  cityDistrictType?: string;
  cityDistrictName?: string;
  settlementType?: string;
  settlementName?: string;
  streetType: string;
  streetName: string;
  houseType: string;
  houseName: string;
  block?: string;
  buildingType?: string;
  buildingName?: string;
  flatType?: string;
  flatName?: string;
  note?: string;
  addressStr: string;
}

export interface ITrustee {
  lastName: string;
  firstName: string;
  patronymic?: string;
  snils: string;
  inn: string;
  trusteeStatus?: string;
  email: string;
  phone: string;
  documents: {
    statusDocument: IDocument;
    authorityDocument: IDocument;
  };
}

export type TMrpaType = 'BY_FILE' | 'BY_GUID';

export interface ISubject {
  role?: string;
  person?: {
    lastName: string;
    firstName: string;
    patronymic?: string;
    birthDate: string;
    birthPlace?: string;
    address: IAddress;
    snils: string;
    inn: string;
    email: string;
    phone?: string;
    isBusinessman: boolean;
    ogrn?: string;
    regDate?: string;
    regPlace?: string;
    emailUl?: string;
    regionCode?: string;
    regionName?: string;
    isTrustee: boolean;
  };
  organization?: {
    name: string;
    address: IAddress;
    addressesMatch: boolean;
    factAddress: IAddress;
    ogrn: string;
    regDate: string;
    inn: string;
    assigneeLastName: string;
    assigneeName: string;
    assigneePatronymic: string;
    kpp: string;
    email: string;
    phone: string;
  };
  foreignOrganization?: {
    name: string;
    address: string;
    inn: string;
    regNumber: string;
    assigneeLastName: string;
    assigneeName: string;
    assigneePatronymic: string;
  };
  changeReason?: string;
  trustee?: ITrustee;
  assignee?: ITrustee;
  jobPlace?: {
    orgName: string;
    fio: string;
    address: IAddress;
    addressesMatch?: boolean;
    factAddress: IAddress;
    ogrn: string;
    inn: string;
    kpp: string;
  };
  identityDocument?: IDocument;
  details?: {
    information: string;
    depositAmount: number;
    depositCurrency: string;
    placePerformanceObligation: string;
    transferTimeLimit?: string;
    transferCondition?: string;
  };
  mrpType?: TMrpaType;
  uiContext?: CommonObject;
}

interface IBankDetails {
  accountNumber: string;
  corrAccountNumber: string;
  bankName: string;
  bic: string;
  recipientName?: string;
  amount?: string;
  currency?: string;
}

export interface IProxy {
  number: number;
  issueDate: string;
  issuePeriodFrom: string;
  issuePeriodTo: string;
  issueNumber: string;
  summary: string;
  trusties: ISubject[];
  proxyPersons: ISubject[];
}

export type ApplicantRole =
  | 'APPLICANT'
  | 'PREVIOUS_CREDITOR'
  | 'CREDITOR_FROM_LIST'
  | 'DEBTOR'
  | 'DEBTOR_FROM_LIST'
  | 'PREVIOUS_DEBTOR'
  | 'TRUSTEE'
  | 'ASSIGNEE'
  | 'PLEDGOR'
  | 'APPLICANT_PLEGOR'
  | 'APPLICANT_PLEDGEE'
  | 'APPLICANT_PLEDGE_MANAGER'
  | 'NOTIFICANT'
  | 'ADDRESSEE';

export interface IStatement {
  id?: string;
  userId?: string;
  statementType?: StatementType;
  statusName?: string;
  subjects: {
    applicant?: ISubject;
    applicantRightholder?: ISubject;
    debtor?: ISubject;
    notificants?: ISubject[];
    debtors?: ISubject[];
    previousCreditors?: ISubject[];
    previousDebtors?: ISubject[];
    pledgor?: ISubject;
    creditors?: ISubject[];
    addressee?: ISubject;
  };
  documents: IDocument[];
  prevCreditorDocuments?: IDocument[];
  prevDebtorDocuments?: IDocument[];
  statementParamDocuments?: IDocument[];
  notarialDocuments?: IDocument[];
  securityEvidenceDetails?: {
    infoLocatedUrl?: string;
    accessInfo?: string;
    informationExamined?: string;
    noSubjectUrgentCase?: boolean;
    noSubjectUnknownSubjectsCase?: boolean;
    note?: string;
    hasPrivilege?: boolean;
  };
  acceptanceDepositDetails?: {
    depositAmount: string;
    depositCurrency: string;
    informationAboutObligation: string;
    note?: string;
    hasPrivilege?: string;
  };
  debtorAccountBankDetails?: IBankDetails;
  applicantAccountBankDetails?: IBankDetails;
  transferDocuments?: IDocument[];
  translatedDocument?: IDocument;
  executiveNoticesRecoveryDetails?: CommonObject;
  judgementCreditorBankDetails?: IBankDetails;
  accountBankDetails?: IBankDetails;
  ruzdiDetails?: CommonObject;
  obligationDocument?: IDocument;
  conversionDocument?: IDocument;
  transferElDocsDetails?: CommonObject;
  correctTranslationDetails?: CommonObject;
  acceptanceEscrowDetails?: CommonObject;
  additionalStatements?: IAdditionalStatements[];
  documentConversionDetails?: CommonObject;
  storageAcceptDetails?: IStorageAcceptDetails;
  storageIssueDetails?: IStorageIssueDetails;
  storageDocument?: IDocument;
  stepsCompleted?: string[];
  rodDetails?: {
    proxies: IProxy[];
    isMachineFill?: boolean;
  };
  extractionRodDetails?: {
    orderNumber: string;
    note: string;
    hasPrivilege: boolean;
  };
  applicantRole?: ApplicantRole;
  egrnConfirmDetails?: CommonObject;
  evidenceDocType?: DocumentType;
  contractDoc?: IDocument;
  preliminaryContractDoc?: IDocument;
  protectionPrivatizationDoc?: IDocument;
  compensationDamageDoc?: IDocument;
  lawsuitRightHolderDoc?: IDocument;
  foreclosureEstateRightholderDoc?: IDocument;
  otherRightholder?: IDocument;
  locationStep?: {
    regionCode: string;
    regionName: string;
  };
}

export enum StepName {
  S1 = 'step1',
  S2 = 'step2',
  S3 = 'step3',
  S4 = 'step4',
  S5 = 'step5',
  S6 = 'step6',
  S7 = 'step7',
  S8 = 'step8',
  DOCUMENTS = 'stepDocuments',
}

export enum StepDirection {
  NEXT = 'next',
  PREV = 'prev',
}

export enum StatementType {
  RUZDI = 'RUZDI',
  NOTICE_RECOVERY = 'NOTICE_RECOVERY',
  SECURING_EVIDENCE = 'SECURING_EVIDENCE',
  ACCEPTANCE_DEPOSIT = 'ACCEPTANCE_DEPOSIT',
  TRANSFER_EL_DOCS = 'TRANSFER_EL_DOCS',
  CORRECT_TRANSLATION = 'CORRECT_TRANSLATION',
  ACCEPTANCE_ESCROW = 'ACCEPTANCE_ESCROW',
  STORAGE_ACCEPT = 'STORAGE_ACCEPT',
  STORAGE_ISSUE = 'STORAGE_ISSUE',
  DOCUMENT_CONVERSION = 'DOCUMENT_CONVERSION',
  ROD = 'ROD',
  EXTRACTION_ROD = 'EXTRACTION_ROD',
  EGRN_CONFIRM = 'EGRN_CONFIRM',
}

export enum MutationType {
  SET_STATEMENT_XML = 'SET_STATEMENT_XML',
}

export type LeaveStep = ({
  stepThis,
  direction,
  step,
}: {
  stepThis: Vue;
  direction: StepDirection;
  step: StepName;
}) => void;

export interface MapStatementData {
  step: StepName;
  fragment: any;
}

export enum DocumentType {
  FILE = 'FILE',
  CONFIRMATION_OF_STATUS = 'CONFIRMATION_OF_STATUS',
  CONFIRMATION_OF_AUTHORITY = 'CONFIRMATION_OF_AUTHORITY',
  IDENTIFYING = 'IDENTIFYING',
  STATEMENT_PARAM = 'STATEMENT_PARAM',
  NOTARIAL = 'NOTARIAL',
  PREV_CREDITOR = 'PREV_CREDITOR',
  PREV_DEBTOR = 'PREV_DEBTOR',
  OBLIGATION = 'OBLIGATION',
  TRANSFER = 'TRANSFER',
  TRANSLATED = 'TRANSLATED',
  FOR_STORAGE = 'FOR_STORAGE',
  FOR_CONVERSION = 'FOR_CONVERSION',
  QUITTANCE = 'QUITTANCE',
  PROXY = 'PROXY',
  MRP = 'MRP',
  CONTRACT = 'CONTRACT',
  PRELIMINARY_CONTRACT = 'PRELIMINARY_CONTRACT',
  PROTECTION_PRIVATIZATION = 'PROTECTION_PRIVATIZATION',
  COMPENSATION_DAMAGE = 'COMPENSATION_DAMAGE',
  LAWSUIT_RIGHTHOLDER = 'LAWSUIT_RIGHTHOLDER',
  FORECLOSURE_ESTATE_RIGHTHOLDER = 'FORECLOSURE_ESTATE_RIGHTHOLDER',
  NOTARY_POWER_ATTORNEY = 'NOTARY_POWER_ATTORNEY',
  OTHER_RIGHTHOLDER = 'OTHER_RIGHTHOLDER',
}

export interface ICreateStatementResponse {
  id: string;
}
